import React from 'react';

import { formatMoney, toMoney } from '@betterment/core';
import { useMutation, useQuery } from '@betterment/js-runtime';
import { Box, Button, Flex, Text } from '@betterment/style-closet-react';

import AccountTypes from './AccountTypes';
import Layout, { StyledCheckbox } from './Layout';
import LinkSent from './LinkSent';

import { CoPilotSectionProvider, useCoPilotSectionState } from '../../contexts/CoPilotSectionContext';
import { useImpersonationModal } from '../../contexts/ImpersonationModalContext';
import { Rmd } from '../../services/api/types/co_pilot/rmds';
import { ActionRequestParams, CoPilotResponseMeta } from '../../utils/co_pilot';

const TableFooter: React.FC<React.PropsWithChildren<unknown>> = () => (
  <Box px="8" py="4">
    <Box mb="2">
      <Text color="bmtGrey50">
        This list only includes eligible IRA accounts, funded as of December 31st of the previous year. Note that
        Betterment does not calculate RMDs for inherited IRA and 401(k) accounts. Learn more about{' '}
        <a href="https://www.betterment.com/help/tag/iras" target="_blank" rel="noopener noreferrer">
          IRA and RMD considerations.
        </a>
      </Text>
    </Box>
    <Box>
      <Text fontSize="1" color="bmtGrey50">
        Betterment is not a tax advisor, nor should any information herein be considered tax advice. Please consult a
        qualified tax professional.
      </Text>
    </Box>
  </Box>
);

const RmdRow: React.FC<
  React.PropsWithChildren<{
    rmd: Rmd;
  }>
> = ({ rmd: { clientName, accountTypes, amountCents, linkSentAt, queuedAt, impersonationLink, id } }) => {
  const { isItemSelected, handleSelectedItemChange } = useCoPilotSectionState<Rmd>();
  const { openModal: openImpersonationModal } = useImpersonationModal();

  return (
    <tr>
      <td>
        <Flex alignItems="center">
          <StyledCheckbox
            name={id}
            id={id}
            role="checkbox"
            type="checkbox"
            className="checkboxInputBase"
            checked={isItemSelected(id)}
            onChange={({ target }: React.ChangeEvent<HTMLInputElement>) => handleSelectedItemChange(target.id)}
          />
          {clientName}
        </Flex>
      </td>
      <AccountTypes accountTypes={accountTypes} />
      <td>{formatMoney(toMoney(amountCents))}</td>
      <LinkSent linkSentAt={linkSentAt} queuedAt={queuedAt} />
      <td>
        <Button
          variant="tertiary"
          onClick={() => {
            openImpersonationModal(impersonationLink);
          }}
        >
          Withdraw
        </Button>
      </td>
    </tr>
  );
};

const RmdsTable: React.FC<React.PropsWithChildren<unknown>> = () => {
  const { itemList, status, handleSelectAllChange, lastPage, loadMore } = useCoPilotSectionState<Rmd>();
  return (
    <table>
      <tbody>
        <tr>
          <Text as="th" fontWeight="medium" pb={6}>
            <Flex alignItems="center">
              <StyledCheckbox
                name="selectAll"
                id="selectAll"
                role="checkbox"
                type="checkbox"
                className="checkboxInputBase"
                checked={status === 'allSelected'}
                onChange={() => handleSelectAllChange()}
              />
              Client
            </Flex>
          </Text>
          <Text as="th" fontWeight="medium" pb={6}>
            Account
          </Text>
          <Text as="th" fontWeight="medium" pb={6}>
            Remaining Amount
          </Text>
          <Text as="th" fontWeight="medium" pb={6}>
            Reminder Sent
          </Text>
          <Text as="th" fontWeight="medium" pb={6}>
            Initiate for client
          </Text>
        </tr>
        {itemList?.map(rmd => (
          <RmdRow key={rmd.id} rmd={rmd} />
        ))}
        {status !== 'empty' && status !== 'fetchError' && !lastPage && (
          <tr>
            <td>
              <Box pb={9}>
                <Button variant="tertiary" onClick={() => loadMore()}>
                  Load more
                </Button>
              </Box>
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
};

const Rmds: React.FC<React.PropsWithChildren<unknown>> = () => {
  const { refetch } = useQuery<Rmd[], CoPilotResponseMeta>(`/api/v1/co_pilot/required_minimum_distributions`, {
    skip: true,
  });

  const [postDismissal] = useMutation<{}, ActionRequestParams>(
    '/api/v1/co_pilot/required_minimum_distributions/dismissals'
  );
  const [postSendReminder] = useMutation<{}, ActionRequestParams>(
    '/api/v1/co_pilot/required_minimum_distributions/reminders'
  );

  return (
    <CoPilotSectionProvider
      fetch={refetch}
      initialOrder={[{ col: 'client_name', dir: 'desc' }]}
      postHandleDismissal={postDismissal}
      postSendReminder={postSendReminder}
    >
      <Layout
        activeTab="Outstanding RMDs"
        header="These clients have not yet withdrawn their required minimum distribution from their retirement goals."
        subHeader="You may want to reach out to these clients to make sure they have made the required distributions from their retirement goals for the year. Be sure to review each clients’ FMV or Form 5498 to confirm the RMD amount. You can access client tax forms by selecting Clients > the desired client > Taxes."
        tableFooter={<TableFooter />}
      >
        <RmdsTable />
      </Layout>
    </CoPilotSectionProvider>
  );
};

export default Rmds;
